import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  PlayCircle,
  Clock,
  ArrowLeft,
  Split,
  Merge,
  Check,
  Sparkles,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { posthog } from 'posthog-js';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import PersonAskAI from './PersonAskAI';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectSeparator,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  getVideoMetadataEntities,
  getVideoMetadataVideos,
  type VideoMetadataVideo,
} from '../../services/videoMetadataService';
import { getFile, getSharedFile } from '../../services/fileSystemService';
import { getFileUrl, getShareNavigationPath } from '../../lib/routingUtils';
import type { FileType } from '../../types';
import type { RootState } from '../../reducers';
import {
  getSpeakerIdentities,
  updateCanonicalName,
  splitSpeaker,
  mergeSpeakers,
  createSpeakerIdentity,
  type SpeakerIdentity,
} from '../../services/speakerIdentityService';

interface PeopleModalProps {
  open: boolean;
  onClose: () => void;
}

interface PersonItem {
  name: string;
  videoCount: number;
  loading?: boolean;
  speakerIdentity?: SpeakerIdentity;
}

interface VideoItem extends VideoMetadataVideo {
  thumbnail?: string;
  duration?: string;
  fileData?: FileType;
  loading?: boolean;
  error?: boolean;
}

const formatRelativeTime = (dateString: string): string => {
  if (!dateString || dateString.trim() === '') {
    return 'Recently';
  }

  const now = new Date();
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    console.warn('Invalid date string:', dateString);
    return 'Recently';
  }

  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return `${Math.floor(diffInDays / 30)} months ago`;
};

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const PeopleModal: React.FC<PeopleModalProps> = ({ open, onClose }) => {
  const [people, setPeople] = useState<PersonItem[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<string | null>(null);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [videosLoading, setVideosLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingAliases, setLoadingAliases] = useState<{
    [key: string]: boolean;
  }>({});

  // Split/Merge state
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState<Set<string>>(
    new Set(),
  );
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [personToSplit, setPersonToSplit] = useState<PersonItem | null>(null);
  const [selectedAliasesForSplit, setSelectedAliasesForSplit] = useState<
    Set<string>
  >(new Set());
  const [newCanonicalNameForSplit, setNewCanonicalNameForSplit] =
    useState<string>('');
  const [showAskAI, setShowAskAI] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();
  const { team } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId;

  const fetchFileDetails = async (recordingId: string) => {
    try {
      const isSharedFile = recordingId.length < 20;
      let fileData;

      if (isSharedFile) {
        fileData = await getSharedFile(recordingId);
      } else {
        fileData = await getFile(recordingId);
      }

      if (fileData?.file) {
        return {
          thumbnail: fileData.file.thumbUrl,
          duration: fileData.file.duration
            ? formatDuration(fileData.file.duration)
            : 'Unknown',
          fileData: fileData.file,
        };
      }
    } catch (fetchError) {
      console.warn(
        'Failed to fetch file details for:',
        recordingId,
        fetchError,
      );
    }
    return null;
  };

  useEffect(() => {
    if (!open || !teamId) return;

    const fetchPeople = async () => {
      try {
        setLoading(true);
        setError(null);

        const entitiesResponse = await getVideoMetadataEntities(teamId);

        if (entitiesResponse.success && entitiesResponse.data) {
          const { speakers, speakersWithCounts } = entitiesResponse.data;

          let peopleWithCounts: PersonItem[];

          // Use counts from API if available, otherwise fallback to individual API calls
          if (speakersWithCounts && speakersWithCounts.length > 0) {
            // Optimized: Use pre-calculated counts from single API call
            peopleWithCounts = speakersWithCounts.map((speaker) => ({
              name: speaker.name,
              videoCount: speaker.count,
            }));

            // Fetch all speaker identities in one API call
            try {
              const speakerIdentitiesResponse =
                await getSpeakerIdentities(teamId);
              console.log(
                'Speaker identities response:',
                speakerIdentitiesResponse,
              );
              if (
                speakerIdentitiesResponse.success &&
                speakerIdentitiesResponse.data
              ) {
                const identitiesMap = new Map<string, SpeakerIdentity>();
                speakerIdentitiesResponse.data.forEach(
                  (identity: SpeakerIdentity) => {
                    // Map both canonical name and aliases to the identity
                    identitiesMap.set(identity.canonicalName, identity);
                    identity.aliases.forEach((alias: string) => {
                      identitiesMap.set(alias, identity);
                    });
                  },
                );

                console.log('Identities map:', identitiesMap);

                // Create a deduplicated people list by speaker identity ID
                const uniquePeopleMap = new Map<string, PersonItem>();

                peopleWithCounts.forEach((person) => {
                  const speakerIdentity = identitiesMap.get(person.name);
                  if (speakerIdentity) {
                    // If we already have this speaker identity, merge the video counts
                    const existingPerson = uniquePeopleMap.get(
                      speakerIdentity._id,
                    );
                    if (existingPerson) {
                      existingPerson.videoCount += person.videoCount;
                    } else {
                      // Use the canonical name as the display name
                      uniquePeopleMap.set(speakerIdentity._id, {
                        name: speakerIdentity.canonicalName,
                        videoCount: person.videoCount,
                        speakerIdentity,
                      });
                    }
                    console.log(
                      `Found speaker identity for ${person.name} -> ${speakerIdentity.canonicalName}:`,
                      speakerIdentity,
                    );
                  } else {
                    // For speakers without identity, use a fallback key
                    const fallbackKey = `no-identity-${person.name}`;
                    uniquePeopleMap.set(fallbackKey, person);
                    console.log(`No speaker identity found for ${person.name}`);
                  }
                });

                // Convert back to array
                peopleWithCounts = Array.from(uniquePeopleMap.values());
              }
            } catch (err) {
              console.log('Error fetching speaker identities:', err);
            }
          } else {
            // Fallback: Make individual API calls (legacy behavior)
            console.warn(
              'Using fallback individual API calls for speaker counts',
            );
            peopleWithCounts = await Promise.all(
              speakers.map(async (speaker) => {
                try {
                  const videosResponse = await getVideoMetadataVideos(teamId, {
                    speakerName: speaker,
                    limit: 1, // Just to get the count
                  });

                  return {
                    name: speaker,
                    videoCount: videosResponse.success
                      ? videosResponse.data.pagination.total
                      : 0,
                  };
                } catch {
                  return {
                    name: speaker,
                    videoCount: 0,
                  };
                }
              }),
            );
          }

          setPeople(
            peopleWithCounts.sort((a, b) => b.videoCount - a.videoCount),
          );
        } else {
          setError('Failed to load people data');
        }
      } catch (err) {
        console.error('Error fetching people:', err);
        setError('Failed to load people');
      } finally {
        setLoading(false);
      }
    };

    fetchPeople();
  }, [open, teamId]);

  // Media query for mobile detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    setIsMobile(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      // Reset to list view when switching to mobile
      if (e.matches) {
        setShowAskAI(false);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handlePersonClick = async (personName: string) => {
    if (!teamId) return;

    posthog.capture('people-modal-person-clicked', {
      personName,
      teamId,
    });

    setSelectedPerson(personName);
    setVideos([]);
    setSearchQuery(''); // Clear search when navigating to person's videos
    setVideosLoading(true);

    try {
      const videosResponse = await getVideoMetadataVideos(teamId, {
        speakerName: personName,
        page: 1,
        limit: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      if (videosResponse.success && videosResponse.data) {
        const { videos: videoList } = videosResponse.data;

        // Create initial video items with loading state
        const videoItems: VideoItem[] = videoList.map((video) => ({
          ...video,
          loading: true,
        }));
        setVideos(videoItems);

        // Batch fetch file details with concurrency limit to reduce API load
        const CONCURRENCY_LIMIT = 5; // Process 5 files at a time instead of all 20+
        const batches = [];
        for (let i = 0; i < videoList.length; i += CONCURRENCY_LIMIT) {
          batches.push(videoList.slice(i, i + CONCURRENCY_LIMIT));
        }

        // Process all batches concurrently (ESLint compliant)
        const allBatchPromises = batches.map((batch, batchIndex) =>
          Promise.allSettled(
            batch.map(async (video, localIndex) => {
              const globalIndex = batchIndex * CONCURRENCY_LIMIT + localIndex;
              const fileDetails = await fetchFileDetails(video.recordingId);
              return { index: globalIndex, fileDetails };
            }),
          ),
        );

        const allBatchResults = await Promise.all(allBatchPromises);

        // Process all results at once
        setVideos((prevVideos) => {
          const updatedVideos = [...prevVideos];
          allBatchResults.flat().forEach((result) => {
            if (result.status === 'fulfilled') {
              const { index, fileDetails } = result.value;
              if (updatedVideos[index]) {
                updatedVideos[index] = {
                  ...updatedVideos[index],
                  ...fileDetails,
                  loading: false,
                  error: !fileDetails,
                };
              }
            }
          });
          return updatedVideos;
        });
      }
    } catch (err) {
      console.error('Error fetching person videos:', err);
      setError('Failed to load videos');
    } finally {
      setVideosLoading(false);
    }
  };

  const handleVideoClick = (video: VideoItem) => {
    if (!teamId) return;

    posthog.capture('people-modal-video-clicked', {
      recordingId: video.recordingId,
      fileName: video.fileName,
      speakerName: selectedPerson,
    });

    onClose();

    const isSharedFile = video.recordingId.length < 20;
    if (isSharedFile) {
      window.location.href = getShareNavigationPath(video.recordingId);
      return;
    }

    const fileUrl = getFileUrl({
      teamId,
      parentId: 'default',
      fileId: video.recordingId,
    });
    navigate(fileUrl);
  };

  const handleBackToPeople = () => {
    setSelectedPerson(null);
    setVideos([]);
    setSearchQuery('');
  };

  const handleUpdateCanonicalName = async (
    person: PersonItem,
    newCanonicalName: string,
  ) => {
    if (!teamId || !person.speakerIdentity) return;

    setLoadingAliases({ ...loadingAliases, [person.name]: true });

    try {
      const response = await updateCanonicalName(
        teamId,
        person.speakerIdentity._id,
        newCanonicalName,
      );

      if (response.success && response.data) {
        const {
          speakerIdentity: updatedSpeakerIdentity,
          previousCanonicalName,
          newCanonicalName: updatedCanonicalName,
        } = response.data;
        // Update the person in the list with the new canonical name
        setPeople((prevPeople) =>
          prevPeople.map((p) => {
            if (
              p.speakerIdentity &&
              person.speakerIdentity &&
              p.speakerIdentity._id === person.speakerIdentity._id
            ) {
              return {
                ...p,
                name: updatedSpeakerIdentity.canonicalName,
                speakerIdentity: updatedSpeakerIdentity,
              };
            }
            return p;
          }),
        );

        toast('Success', {
          description: `Updated speaker name from "${previousCanonicalName}" to "${updatedCanonicalName}"`,
        });
      }
    } catch (err) {
      console.error('Error updating canonical name:', err);
      toast.error('Failed to update speaker name');
    } finally {
      setLoadingAliases({ ...loadingAliases, [person.name]: false });
    }
  };

  const handleOpenSplitDialog = (person: PersonItem) => {
    if (
      !person.speakerIdentity ||
      person.speakerIdentity.aliases.length === 0
    ) {
      toast.error('This speaker has no aliases to split');
      return;
    }
    setPersonToSplit(person);
    setSelectedAliasesForSplit(new Set());
    setNewCanonicalNameForSplit('');
    setSplitDialogOpen(true);
  };

  const handleSplitSpeaker = async () => {
    if (!teamId || !personToSplit?.speakerIdentity) return;

    if (selectedAliasesForSplit.size === 0) {
      toast.error('Please select at least one alias to split');
      return;
    }

    if (!newCanonicalNameForSplit) {
      toast.error('Please select a canonical name for the new speaker');
      return;
    }

    try {
      const response = await splitSpeaker(
        teamId,
        personToSplit.speakerIdentity._id,
        Array.from(selectedAliasesForSplit),
        newCanonicalNameForSplit,
      );

      if (response.success && response.data) {
        // Refresh the people list
        const entitiesResponse = await getVideoMetadataEntities(teamId);
        if (entitiesResponse.success && entitiesResponse.data) {
          const speakerIdentitiesResponse = await getSpeakerIdentities(teamId);
          if (
            speakerIdentitiesResponse.success &&
            speakerIdentitiesResponse.data
          ) {
            const identitiesMap = new Map<string, SpeakerIdentity>();
            speakerIdentitiesResponse.data.forEach(
              (identity: SpeakerIdentity) => {
                identitiesMap.set(identity.canonicalName, identity);
                identity.aliases.forEach((alias: string) => {
                  identitiesMap.set(alias, identity);
                });
              },
            );

            const uniquePeopleMap = new Map<string, PersonItem>();
            entitiesResponse.data.speakersWithCounts?.forEach(
              (speaker: any) => {
                const speakerIdentity = identitiesMap.get(speaker.name);
                if (speakerIdentity) {
                  const existingPerson = uniquePeopleMap.get(
                    speakerIdentity._id,
                  );
                  if (existingPerson) {
                    existingPerson.videoCount += speaker.count;
                  } else {
                    uniquePeopleMap.set(speakerIdentity._id, {
                      name: speakerIdentity.canonicalName,
                      videoCount: speaker.count,
                      speakerIdentity,
                    });
                  }
                } else {
                  const fallbackKey = `no-identity-${speaker.name}`;
                  uniquePeopleMap.set(fallbackKey, {
                    name: speaker.name,
                    videoCount: speaker.count,
                  });
                }
              },
            );

            setPeople(
              Array.from(uniquePeopleMap.values()).sort(
                (a, b) => b.videoCount - a.videoCount,
              ),
            );
          }
        }

        toast('Success', {
          description: `Split speaker "${personToSplit.name}" successfully`,
        });
        setSplitDialogOpen(false);
        setPersonToSplit(null);
      }
    } catch (err) {
      console.error('Error splitting speaker:', err);
      toast.error('Failed to split speaker');
    }
  };

  const handleToggleMergeMode = async () => {
    if (!teamId) return;

    const newMergeMode = !mergeMode;
    setMergeMode(newMergeMode);
    setSelectedForMerge(new Set());

    // When entering merge mode, create speaker identities for speakers without them
    if (newMergeMode) {
      const speakersWithoutIdentity = people.filter((p) => !p.speakerIdentity);

      if (speakersWithoutIdentity.length > 0) {
        try {
          // Create speaker identities for speakers without one
          const createPromises = speakersWithoutIdentity.map((person) =>
            createSpeakerIdentity(teamId, person.name, []),
          );

          await Promise.all(createPromises);

          // Refresh the people list to get the newly created identities
          const entitiesResponse = await getVideoMetadataEntities(teamId);
          if (entitiesResponse.success && entitiesResponse.data) {
            const speakerIdentitiesResponse =
              await getSpeakerIdentities(teamId);
            if (
              speakerIdentitiesResponse.success &&
              speakerIdentitiesResponse.data
            ) {
              const identitiesMap = new Map<string, SpeakerIdentity>();
              speakerIdentitiesResponse.data.forEach(
                (identity: SpeakerIdentity) => {
                  identitiesMap.set(identity.canonicalName, identity);
                  identity.aliases.forEach((alias: string) => {
                    identitiesMap.set(alias, identity);
                  });
                },
              );

              const uniquePeopleMap = new Map<string, PersonItem>();
              entitiesResponse.data.speakersWithCounts?.forEach(
                (speaker: any) => {
                  const speakerIdentity = identitiesMap.get(speaker.name);
                  if (speakerIdentity) {
                    const existingPerson = uniquePeopleMap.get(
                      speakerIdentity._id,
                    );
                    if (existingPerson) {
                      existingPerson.videoCount += speaker.count;
                    } else {
                      uniquePeopleMap.set(speakerIdentity._id, {
                        name: speakerIdentity.canonicalName,
                        videoCount: speaker.count,
                        speakerIdentity,
                      });
                    }
                  } else {
                    const fallbackKey = `no-identity-${speaker.name}`;
                    uniquePeopleMap.set(fallbackKey, {
                      name: speaker.name,
                      videoCount: speaker.count,
                    });
                  }
                },
              );

              setPeople(
                Array.from(uniquePeopleMap.values()).sort(
                  (a, b) => b.videoCount - a.videoCount,
                ),
              );
            }
          }
        } catch (err) {
          console.error('Error creating speaker identities:', err);
          toast.error('Some speaker identities could not be created');
        }
      }
    }
  };

  const handleToggleSelectForMerge = (person: PersonItem) => {
    if (!person.speakerIdentity) return;

    const newSelected = new Set(selectedForMerge);
    if (newSelected.has(person.speakerIdentity._id)) {
      newSelected.delete(person.speakerIdentity._id);
    } else {
      newSelected.add(person.speakerIdentity._id);
    }
    setSelectedForMerge(newSelected);

    // Exit merge mode if nothing is selected
    if (newSelected.size === 0) {
      setMergeMode(false);
    }
  };

  const handleOpenMergeDialog = () => {
    if (selectedForMerge.size < 2) {
      toast.error('Please select at least 2 speakers to merge');
      return;
    }
    setMergeDialogOpen(true);
  };

  const handleMergeSpeakers = async (targetCanonicalName: string) => {
    if (!teamId) return;

    try {
      const response = await mergeSpeakers(
        teamId,
        Array.from(selectedForMerge),
        targetCanonicalName,
      );

      if (response.success && response.data) {
        // Refresh the people list
        const entitiesResponse = await getVideoMetadataEntities(teamId);
        if (entitiesResponse.success && entitiesResponse.data) {
          const speakerIdentitiesResponse = await getSpeakerIdentities(teamId);
          if (
            speakerIdentitiesResponse.success &&
            speakerIdentitiesResponse.data
          ) {
            const identitiesMap = new Map<string, SpeakerIdentity>();
            speakerIdentitiesResponse.data.forEach(
              (identity: SpeakerIdentity) => {
                identitiesMap.set(identity.canonicalName, identity);
                identity.aliases.forEach((alias: string) => {
                  identitiesMap.set(alias, identity);
                });
              },
            );

            const uniquePeopleMap = new Map<string, PersonItem>();
            entitiesResponse.data.speakersWithCounts?.forEach(
              (speaker: any) => {
                const speakerIdentity = identitiesMap.get(speaker.name);
                if (speakerIdentity) {
                  const existingPerson = uniquePeopleMap.get(
                    speakerIdentity._id,
                  );
                  if (existingPerson) {
                    existingPerson.videoCount += speaker.count;
                  } else {
                    uniquePeopleMap.set(speakerIdentity._id, {
                      name: speakerIdentity.canonicalName,
                      videoCount: speaker.count,
                      speakerIdentity,
                    });
                  }
                } else {
                  const fallbackKey = `no-identity-${speaker.name}`;
                  uniquePeopleMap.set(fallbackKey, {
                    name: speaker.name,
                    videoCount: speaker.count,
                  });
                }
              },
            );

            setPeople(
              Array.from(uniquePeopleMap.values()).sort(
                (a, b) => b.videoCount - a.videoCount,
              ),
            );
          }
        }

        toast('Success', {
          description: `Merged ${selectedForMerge.size} speakers successfully`,
        });
        setMergeDialogOpen(false);
        setMergeMode(false);
        setSelectedForMerge(new Set());
      }
    } catch (err) {
      console.error('Error merging speakers:', err);
      toast.error('Failed to merge speakers');
    }
  };

  const filteredPeople = people.filter((person) =>
    person.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredVideos = videos.filter((video) =>
    video.fileName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Get file IDs for the selected person
  const selectedPersonFileIds = videos
    .filter((v) => v.recordingId)
    .map((v) => v.recordingId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'h-[480px] max-h-[80vh] overflow-hidden focus:outline-none focus:ring-0 p-0 gap-0 flex flex-col',
          selectedPerson && !isMobile ? 'sm:max-w-[900px]' : 'sm:max-w-[600px]',
        )}
      >
        {/* Search Header */}
        {selectedPerson ? (
          <div>
            <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackToPeople}
                className="h-8 w-8 -ml-2 flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h2 className="font-semibold text-base">{selectedPerson}</h2>
              {/* Mobile toggle button for Ask AI - positioned absolutely to match X button */}
              {isMobile && selectedPerson && teamId && (
                <button
                  type="button"
                  onClick={() => setShowAskAI(!showAskAI)}
                  className="absolute right-12 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <Sparkles className="h-4 w-4" />
                  <span className="sr-only">Toggle Ask AI</span>
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
              <Search className="h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent outline-none text-base"
                disabled={loading || videosLoading}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search people..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-base"
              disabled={loading || videosLoading}
            />
          </div>
        )}

        {/* Merge Mode Actions - Fixed at bottom */}
        {mergeMode && (
          <div className="absolute bottom-0 left-0 right-0 px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-background flex items-center justify-between z-10">
            <p className="text-sm text-muted-foreground">
              {selectedForMerge.size} selected
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleToggleMergeMode}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleOpenMergeDialog}
                disabled={selectedForMerge.size < 2}
              >
                Merge
              </Button>
            </div>
          </div>
        )}

        {/* Content - Split view when person is selected */}
        <div className="flex-1 flex overflow-hidden">
          {/* List View - Hide on mobile when Ask AI is shown */}
          <div
            className={cn(
              'overflow-y-auto overflow-x-hidden',
              mergeMode ? 'pb-16' : '',
              selectedPerson && !isMobile ? 'flex-1' : 'w-full',
              isMobile && showAskAI ? 'hidden' : '',
            )}
          >
            {(() => {
              if (loading || videosLoading) {
                return (
                  <div className="pt-3">
                    <div className="space-y-2">
                      {Array.from({ length: 8 }, (_, i) => (
                        <div key={i} className="flex gap-3 px-6 py-2.5">
                          {selectedPerson ? (
                            <>
                              <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                              <div className="flex-1 space-y-1">
                                <Skeleton className="h-4 w-full max-w-[70%]" />
                                <Skeleton className="h-3 w-full max-w-[40%]" />
                              </div>
                            </>
                          ) : (
                            <>
                              <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
                              <div className="flex-1 space-y-1 pt-1">
                                <Skeleton className="h-4 w-full max-w-[60%]" />
                                <Skeleton className="h-3 w-full max-w-[30%]" />
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              if (error) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium text-destructive">
                        {error}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Please try refreshing the page
                      </p>
                    </div>
                  </div>
                );
              }

              if (selectedPerson) {
                // Show videos for selected person
                if (filteredVideos.length === 0) {
                  return (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-sm text-gray-500">
                        {searchQuery
                          ? 'No videos found'
                          : 'No videos available'}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="pt-3">
                    {filteredVideos.map((video, index) => (
                      <button
                        key={`${video.recordingId}-${index}`}
                        onClick={() => handleVideoClick(video)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleVideoClick(video);
                          }
                        }}
                        type="button"
                        className="w-full text-left px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-3"
                      >
                        <div className="relative flex-shrink-0">
                          {video.thumbnail ? (
                            <img
                              src={video.thumbnail}
                              alt={video.fileName}
                              className="w-12 h-8 object-cover rounded bg-gray-100 dark:bg-gray-800"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const wrapper = target.parentElement!;
                                wrapper.className =
                                  'w-12 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0';
                                wrapper.innerHTML =
                                  '<svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>';
                              }}
                            />
                          ) : (
                            <div className="w-12 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                              <PlayCircle className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm line-clamp-1">
                              {video.loading ? (
                                <span className="flex items-center gap-1.5 min-w-[100px]">
                                  <Clock className="w-3 h-3 flex-shrink-0" />
                                  <span>Loading...</span>
                                </span>
                              ) : (
                                video.fileName
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {formatRelativeTime(video.createdAt)}
                            </div>
                          </div>
                          {video.duration && video.duration !== 'Unknown' && (
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {video.duration}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              }

              // Show people list
              if (filteredPeople.length === 0) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-500">
                      {searchQuery
                        ? 'No people found'
                        : 'No people data available'}
                    </p>
                  </div>
                );
              }

              return (
                <div className="pt-3">
                  {filteredPeople.map((person) => (
                    <div
                      key={person.name}
                      className="group/row w-full text-left px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-3"
                    >
                      <button
                        onClick={() => {
                          if (!mergeMode) {
                            handleToggleMergeMode();
                          }
                          handleToggleSelectForMerge(person);
                        }}
                        type="button"
                        className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0 hover:bg-muted/80 cursor-pointer group relative"
                      >
                        {mergeMode &&
                        person.speakerIdentity &&
                        selectedForMerge.has(person.speakerIdentity._id) ? (
                          <div className="h-4 w-4 shrink-0 rounded-sm border border-primary shadow bg-primary text-primary-foreground flex items-center justify-center">
                            <Check className="h-4 w-4" />
                          </div>
                        ) : (
                          <>
                            <Users className="w-4 h-4 text-muted-foreground group-hover:hidden" />
                            <div className="h-4 w-4 shrink-0 rounded-sm border border-primary shadow hidden group-hover:block" />
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handlePersonClick(person.name)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePersonClick(person.name);
                          }
                        }}
                        type="button"
                        className="flex-1 flex items-center gap-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded min-w-0"
                        disabled={mergeMode}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-sm">
                              {person.name}
                            </h3>
                            {person.speakerIdentity &&
                              person.speakerIdentity.aliases.length > 0 && (
                                <Select
                                  value={person.speakerIdentity.canonicalName}
                                  onValueChange={(value) => {
                                    if (value === '__split__') {
                                      handleOpenSplitDialog(person);
                                    } else if (value === '__merge__') {
                                      if (!mergeMode) {
                                        handleToggleMergeMode();
                                      }
                                      handleToggleSelectForMerge(person);
                                    } else {
                                      handleUpdateCanonicalName(person, value);
                                    }
                                  }}
                                  disabled={
                                    loadingAliases[person.name] || mergeMode
                                  }
                                >
                                  <SelectTrigger
                                    className="h-5 w-auto px-1.5 py-0.5 text-[10px] gap-1 border-0 bg-muted hover:bg-muted/80"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <span className="text-muted-foreground">
                                      {person.speakerIdentity.aliases.length}{' '}
                                      alias
                                      {person.speakerIdentity.aliases.length !==
                                      1
                                        ? 'es'
                                        : ''}
                                    </span>
                                  </SelectTrigger>
                                  <SelectContent
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <SelectItem
                                      value={
                                        person.speakerIdentity.canonicalName
                                      }
                                    >
                                      {person.speakerIdentity.canonicalName}{' '}
                                      (current)
                                    </SelectItem>
                                    {person.speakerIdentity.aliases.map(
                                      (alias) => (
                                        <SelectItem key={alias} value={alias}>
                                          {alias}
                                        </SelectItem>
                                      ),
                                    )}
                                    <SelectSeparator />
                                    <SelectItem value="__merge__">
                                      <div className="flex items-center">
                                        <Merge className="h-4 w-4 mr-2" />
                                        Merge with others
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="__split__">
                                      <div className="flex items-center">
                                        <Split className="h-4 w-4 mr-2" />
                                        Split speaker
                                      </div>
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground flex-shrink-0">
                          {person.videoCount}{' '}
                          {person.videoCount === 1 ? 'video' : 'videos'}
                        </p>
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Ask AI Panel - Only show when person is selected */}
          {selectedPerson && teamId && (
            <div
              className={cn(
                'flex-shrink-0',
                isMobile ? 'w-full' : 'w-[350px]',
                isMobile && !showAskAI ? 'hidden' : '',
              )}
            >
              <PersonAskAI
                personName={selectedPerson}
                fileIds={selectedPersonFileIds}
                teamId={teamId}
              />
            </div>
          )}
        </div>
      </DialogContent>

      {/* Split Speaker Dialog */}
      <AlertDialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Split Speaker: {personToSplit?.name}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Select aliases to move to a new speaker identity. At least one
              alias must remain with the original speaker.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">Select aliases to split:</p>
              {personToSplit?.speakerIdentity?.aliases.map((alias) => (
                <div key={alias} className="flex items-center space-x-2">
                  <Checkbox
                    id={`alias-${alias}`}
                    checked={selectedAliasesForSplit.has(alias)}
                    onCheckedChange={(checked) => {
                      const newSelected = new Set(selectedAliasesForSplit);
                      if (checked) {
                        newSelected.add(alias);
                      } else {
                        newSelected.delete(alias);
                      }
                      setSelectedAliasesForSplit(newSelected);
                    }}
                  />
                  <label
                    htmlFor={`alias-${alias}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {alias}
                  </label>
                </div>
              ))}
            </div>
            {selectedAliasesForSplit.size > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Select canonical name for new speaker:
                </p>
                <Select
                  value={newCanonicalNameForSplit}
                  onValueChange={setNewCanonicalNameForSplit}
                >
                  <SelectTrigger>
                    <span>
                      {newCanonicalNameForSplit || 'Select a name...'}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from(selectedAliasesForSplit).map((alias) => (
                      <SelectItem key={alias} value={alias}>
                        {alias}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSplitSpeaker}>
              Split Speaker
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Merge Speakers Dialog */}
      <AlertDialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge Speakers</AlertDialogTitle>
            <AlertDialogDescription>
              Select a canonical name for the merged speaker. All aliases from
              selected speakers will be combined.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">
                Selected speakers ({selectedForMerge.size}):
              </p>
              <ul className="text-sm text-muted-foreground list-disc list-inside">
                {Array.from(selectedForMerge).map((speakerId) => {
                  const person = people.find(
                    (p) => p.speakerIdentity?._id === speakerId,
                  );
                  return person ? <li key={speakerId}>{person.name}</li> : null;
                })}
              </ul>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">
                Select canonical name for merged speaker:
              </p>
              <Select
                onValueChange={(value) => {
                  handleMergeSpeakers(value);
                }}
              >
                <SelectTrigger>
                  <span>Select a name...</span>
                </SelectTrigger>
                <SelectContent>
                  {Array.from(selectedForMerge).map((speakerId) => {
                    const person = people.find(
                      (p) => p.speakerIdentity?._id === speakerId,
                    );
                    if (!person?.speakerIdentity) return null;
                    return [
                      <SelectItem
                        key={person.speakerIdentity.canonicalName}
                        value={person.speakerIdentity.canonicalName}
                      >
                        {person.speakerIdentity.canonicalName}
                      </SelectItem>,
                      ...person.speakerIdentity.aliases.map((alias) => (
                        <SelectItem key={`${speakerId}-${alias}`} value={alias}>
                          {alias}
                        </SelectItem>
                      )),
                    ];
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default PeopleModal;
